// Helpers for the dashboard's weekly attendance ("Daily presence") trend.
//
// Background — why this exists:
// The backend stores one AttendanceStatus enum per row. Those values fall into
// two groups:
//   • transactional (LIVE)   — clock_in / clock_out / additional_in / additional_out
//   • finalized (END OF DAY) — present / absent / late / half_day / work_from_home
//
// A normal clock-in writes status="clock_in", and only an end-of-day batch (or an
// admin "mark present") rewrites it to "present". So if the dashboard counts only
// the finalized present statuses, *today's* attendance — which is still "clock_in"
// — is invisible and the bar reads 0, while older finalized days still show counts.
//
// The backend's own attendance_status() derives presence purely from whether a
// user has a clock-in that day (`if first_in: status="Present"`). We mirror that
// exact rule here so the trend agrees with the rest of the app.

export type AttendanceRow = {
  user_id?: string | null;
  id?: string | null;
  status?: string | null;
  clock_in_time?: string | null;
};

// Finalized statuses that mean "was present" even when the row carries no
// clock_in_time (e.g. an admin "mark present", or a batch-finalized record).
const FINALIZED_PRESENT_STATUSES = new Set([
  "present",
  "late",
  "half_day",
  "work_from_home",
]);

// True when a single attendance row indicates the user actually showed up.
// Presence = they have a clock-in (covers every live status: clock_in,
// additional_in, and WFH clock-ins all set clock_in_time) OR the row was
// finalized to a present-type status. An explicit "absent" row (no clock-in)
// and a bare clock-out row (clock_out, no clock-in) both correctly fail this.
export function isPresentRow(row: AttendanceRow | null | undefined): boolean {
  if (!row) return false;
  if (row.clock_in_time) return true;
  return FINALIZED_PRESENT_STATUSES.has(String(row.status ?? "").toLowerCase());
}

// Count DISTINCT present users for a single day's attendance rows.
// Dedupes by user_id so multiple rows per user (clock-in + clock-out, or several
// additional in/out punches) only ever count once.
export function countPresentForDay(rows: AttendanceRow[] | null | undefined): number {
  const present = new Set<string>();
  (Array.isArray(rows) ? rows : []).forEach((row, i) => {
    if (isPresentRow(row)) {
      present.add(String(row.user_id ?? row.id ?? `row-${i}`));
    }
  });
  return present.size;
}
